import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../AuthProvider';
import {
  allocateNumber,
  getApiErrorMessage,
  listAvailableNumbers,
  resolveCompanyId,
} from './CallCenterService';
import './CallCenter.css';

const numberValue = (row) => (
  row?.phoneNumber
  || row?.e164PhoneNumber
  || row?.e164Number
  || row?.number
  || ''
);

const statusValue = (row) => row?.status || row?.numberStatus || row?.state || 'AVAILABLE';

export default function AvailableNumbersPage() {
  const authContext = useAuth();
  const companyId = useMemo(() => resolveCompanyId(authContext?.additionalUserInfo), [authContext?.additionalUserInfo]);

  const [numbers, setNumbers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [allocating, setAllocating] = useState('');

  const fetchNumbers = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const list = await listAvailableNumbers(authContext.token, companyId);
      setNumbers(Array.isArray(list) ? list : []);
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authContext?.token) {
      fetchNumbers();
    }
  }, [authContext?.token, companyId]);

  const onAllocate = async (row) => {
    const phoneNumber = numberValue(row);
    if (!phoneNumber) {
      setError('Selected row does not contain a phone number field.');
      return;
    }

    const confirmed = window.confirm(`Allocate number ${phoneNumber}?`);
    if (!confirmed) return;

    setAllocating(phoneNumber);
    setError('');
    setSuccess('');

    try {
      await allocateNumber(authContext.token, companyId, { phoneNumber });
      setSuccess(`Number ${phoneNumber} allocated successfully.`);
      await fetchNumbers();
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setAllocating('');
    }
  };

  return (
    <div className="call-center-page">
      <h1 className="call-center-title">Available Numbers</h1>
      <p className="call-center-subtitle">List available AWS Connect numbers and allocate one.</p>

      <div className="call-center-actions">
        <button className="call-center-btn call-center-btn-secondary" onClick={fetchNumbers} disabled={loading}>
          {loading ? 'Refreshing...' : 'Refresh List'}
        </button>
      </div>

      {error && <div className="call-center-message call-center-message-error">{error}</div>}
      {success && <div className="call-center-message call-center-message-success">{success}</div>}

      <div className="call-center-card call-center-table-wrap">
        <table className="call-center-table">
          <thead>
            <tr>
              <th>Phone Number</th>
              <th>Status</th>
              <th>Region</th>
              <th>Type</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {!loading && numbers.length === 0 && (
              <tr>
                <td colSpan="5">No numbers returned by API.</td>
              </tr>
            )}

            {numbers.map((row, idx) => {
              const phone = numberValue(row) || `row-${idx + 1}`;
              const status = statusValue(row);
              return (
                <tr key={`${phone}-${idx}`}>
                  <td>{numberValue(row) || '-'}</td>
                  <td><span className="call-center-badge">{status}</span></td>
                  <td>{row?.region || row?.countryCode || '-'}</td>
                  <td>{row?.type || row?.phoneNumberType || '-'}</td>
                  <td>
                    <button
                      className="call-center-btn"
                      onClick={() => onAllocate(row)}
                      disabled={allocating === numberValue(row)}
                    >
                      {allocating === numberValue(row) ? 'Allocating...' : 'Allocate'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
